// MyClientsGrid — folder-grid layout for the My Clients dashboard.
//
// One folder card per LINEAGE (rootClientId), so Smith / Smith 2 / Smith 3 collapse
// into one stacked folder labeled "Smith (3)". Click a single-version folder
// → onClientView directly. Click a multi-version stack → onLineageOpen, which
// the parent uses to render the existing row-list scoped to that lineage (so
// Compare / Add Scenario / Duplicate buttons are immediately reachable without
// drilling into Client View first).

import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, CheckSquare, FolderClosed, Pencil, Plus, Search as SearchIcon, Square, Trash2, Users } from 'lucide-react';
import { formatDistanceToNow, format as formatDate } from 'date-fns';
import type { TaxScenario } from '@/tax/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { isTourPopover } from '@/tutorial/engine';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

type SortMode = 'newest' | 'oldest' | 'recently-edited' | 'name-asc' | 'name-desc';
const SORT_MODES: SortMode[] = ['newest', 'oldest', 'recently-edited', 'name-asc', 'name-desc'];

// The grid unmounts every time the advisor opens a folder, so the sort choice
// lives in localStorage (per grid — My Clients and All Clients remember
// separately) instead of being reset to "Newest first" on every return.
const sortStorageKey = (isOrgView: boolean) => `taxgenie.clientsSort.${isOrgView ? 'org' : 'personal'}`;
function readStoredSort(isOrgView: boolean): SortMode {
  try {
    const raw = localStorage.getItem(sortStorageKey(isOrgView));
    return SORT_MODES.includes(raw as SortMode) ? (raw as SortMode) : 'newest';
  } catch {
    return 'newest';
  }
}
function writeStoredSort(isOrgView: boolean, mode: SortMode) {
  try {
    localStorage.setItem(sortStorageKey(isOrgView), mode);
  } catch {
    // Storage unavailable (private mode, quota) — the in-memory choice still applies.
  }
}

interface ClientMember {
  clientId: string;
  rootClientId: string;
  name: string;
  scenarioCount: number;
  baselineCreatedAt: string;
  lastEdit: string;
  userId: string;
}

interface Lineage {
  rootId: string;
  rootName: string;
  members: ClientMember[];
  lastEdit: string;
  anchorCreatedAt: string;
  /** Owner user_id from the root member — used for the "by {agent}" badge and agent filter in org view. */
  creatorId: string;
}

interface MyClientsGridProps {
  scenarios: TaxScenario[];
  drafts: TaxScenario[];
  onNewScenario: () => void;
  /** Single-version folders open straight to Client View for that one client. */
  onClientView: (clientId: string) => void;
  /** Multi-version folders open the lineage's row-list (with Compare / Add / Duplicate). */
  onLineageOpen: (rootClientId: string) => void;
  /** Org-scoped view: shows the agent filter dropdown and a "by {agent}" badge on each folder. */
  isOrgView?: boolean;
  /** user_id → display info; used by the agent filter dropdown and folder badges. */
  creatorById?: Map<string, { full_name: string | null; email: string | null }>;
  /**
   * Merge handler. When set, a pencil icon appears next to the count so the
   * user can enter edit mode. Two entry points fire this callback:
   *   - Drag a folder onto another → merge dialog. If the dragged folder is
   *     part of the current selection, the whole selection merges into the target.
   *   - Select 2+ folders (click in edit mode) → multi-lineage merge dialog
   * In both cases the callback receives the kept root and the list of roots
   * being merged INTO it. Omit to disable the merge feature (used in org view).
   */
  onMergeLineages?: (keepRootId: string, mergeRootIds: string[]) => Promise<void> | void;
  /**
   * Bulk-delete handler. When set together with onMergeLineages, edit mode
   * also offers a "Delete N folders" action for the current selection. The
   * callback receives the rootIds of every selected lineage; the parent is
   * responsible for fanning out to scenario-level deletes (we just pass roots).
   * Omit to hide the delete action.
   */
  onDeleteLineages?: (rootIds: string[]) => Promise<void> | void;
  /**
   * Org view only: when set (together with a non-empty advisors list), a
   * "New Client" button appears that opens an advisor picker instead of
   * going straight to onNewScenario. Fired with the chosen advisor's user id.
   */
  onNewClientFor?: (userId: string) => void;
  /** Org view only: the pickable advisors (org members) for onNewClientFor's dialog. */
  advisors?: { id: string; label: string }[];
}

function creatorLabel(creator: { full_name: string | null; email: string | null } | undefined): string {
  return creator?.full_name?.trim() || creator?.email || 'Unknown';
}

function groupLineages(scenarios: TaxScenario[], drafts: TaxScenario[], sortMode: SortMode): Lineage[] {
  // First group by clientId to form member entries.
  const byClient = new Map<string, ClientMember>();
  for (const s of scenarios) {
    const existing = byClient.get(s.clientId);
    if (!existing) {
      byClient.set(s.clientId, {
        clientId: s.clientId,
        rootClientId: s.rootClientId,
        name: s.input.name,
        scenarioCount: 1,
        baselineCreatedAt: s.createdAt,
        lastEdit: s.updatedAt,
        userId: s.userId,
      });
    } else {
      existing.scenarioCount += 1;
      if (s.updatedAt > existing.lastEdit) existing.lastEdit = s.updatedAt;
      // Baseline (sortOrder=0) wins for the display name + createdAt.
      if ((s.sortOrder ?? 0) === 0) {
        existing.name = s.input.name;
        existing.baselineCreatedAt = s.createdAt;
      }
    }
  }
  for (const d of drafts) {
    const existing = byClient.get(d.clientId);
    if (existing) {
      existing.scenarioCount += 1;
      if (d.updatedAt > existing.lastEdit) existing.lastEdit = d.updatedAt;
    }
  }

  // Then group those members into lineages.
  const byLineage = new Map<string, ClientMember[]>();
  for (const m of byClient.values()) {
    const arr = byLineage.get(m.rootClientId) ?? [];
    arr.push(m);
    byLineage.set(m.rootClientId, arr);
  }

  const lineages: Lineage[] = Array.from(byLineage.entries()).map(([rootId, members]) => {
    members.sort((a, b) => a.baselineCreatedAt.localeCompare(b.baselineCreatedAt));
    const root = members.find(m => m.clientId === rootId) ?? members[0];
    const lastEdit = members.reduce((max, m) => (m.lastEdit > max ? m.lastEdit : max), '');
    return {
      rootId,
      rootName: root.name,
      members,
      lastEdit: lastEdit || root.baselineCreatedAt,
      anchorCreatedAt: root.baselineCreatedAt,
      creatorId: root.userId,
    };
  });

  switch (sortMode) {
    case 'oldest':
      lineages.sort((a, b) => a.anchorCreatedAt.localeCompare(b.anchorCreatedAt));
      break;
    case 'recently-edited':
      lineages.sort((a, b) => b.lastEdit.localeCompare(a.lastEdit));
      break;
    case 'name-asc':
      lineages.sort((a, b) => a.rootName.localeCompare(b.rootName, undefined, { sensitivity: 'base' }));
      break;
    case 'name-desc':
      lineages.sort((a, b) => b.rootName.localeCompare(a.rootName, undefined, { sensitivity: 'base' }));
      break;
    case 'newest':
    default:
      lineages.sort((a, b) => b.anchorCreatedAt.localeCompare(a.anchorCreatedAt));
  }
  return lineages;
}

// Visual folder card. Stacked appearance comes from 1-2 phantom layers behind
// the primary card, each rotated slightly so they peek out at the edges.
function FolderCard({
  title,
  versionCount,
  scenarioCount,
  lastEdit,
  layoutId,
  onClick,
  creatorName,
  draggable,
  isDragSource,
  isDropTarget,
  selectable,
  isSelected,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}: {
  title: string;
  versionCount: number;
  scenarioCount: number;
  lastEdit?: string;
  layoutId?: string;
  onClick: () => void;
  creatorName?: string;
  draggable?: boolean;
  isDragSource?: boolean;
  isDropTarget?: boolean;
  /** True when edit mode is on — render the selection checkbox in the corner. */
  selectable?: boolean;
  /** True when this folder is in the current multi-select set. */
  isSelected?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  onDragEnter?: (e: React.DragEvent) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
}) {
  const hasStack = versionCount > 1;
  return (
    <motion.button
      layoutId={layoutId}
      whileHover={draggable ? undefined : { y: -3 }}
      whileTap={draggable ? undefined : { scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      onClick={onClick}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      // w-full h-full: a <button> shrink-wraps its content; this keeps the card
      // exactly one grid column wide so long client names truncate.
      className={`relative w-full h-full text-left group ${draggable ? 'cursor-grab active:cursor-grabbing' : ''} ${isDragSource ? 'opacity-40' : ''} ${isDropTarget ? 'rounded-lg ring-2 ring-primary ring-offset-2' : ''} ${isSelected ? 'rounded-lg ring-2 ring-primary ring-offset-2' : ''}`}
      aria-label={`Open ${title}${hasStack ? ` (${versionCount} versions)` : ''}`}
      aria-pressed={selectable ? isSelected : undefined}
      data-tour="client-folder"
    >
      {/* Stack layers — render behind the primary card. Each rotated a bit so
          they peek out like fanned papers. Limited to 2 phantom layers so it
          reads "stack" without looking like a card deck. */}
      {hasStack && (
        <>
          <div
            aria-hidden
            className="absolute inset-0 rounded-lg bg-primary/5 border border-primary/20 shadow-sm transform translate-x-1 translate-y-1 sm:translate-x-1.5 sm:translate-y-1.5 rotate-[2deg]"
          />
          {versionCount >= 3 && (
            <div
              aria-hidden
              className="absolute inset-0 rounded-lg bg-primary/10 border border-primary/20 shadow-sm transform translate-x-0.5 translate-y-0.5 -rotate-[1deg]"
            />
          )}
        </>
      )}

      {/* Primary folder card */}
      <div className="relative rounded-lg bg-card border border-border shadow-sm group-hover:shadow-md group-hover:border-primary/40 transition-all overflow-visible">
        {/* Folder "tab" sticking out the top-left */}
        <div className="absolute -top-1.5 left-3 h-2 w-10 sm:-top-2 sm:left-4 sm:h-3 sm:w-14 rounded-t-md bg-primary/15 border border-primary/30 border-b-0" />

        {/* Selection checkbox — only in edit mode, top-right corner. */}
        {selectable && (
          <span
            aria-hidden
            className={`absolute top-1.5 right-2 sm:top-2 sm:right-3 inline-flex items-center justify-center h-4 w-4 sm:h-5 sm:w-5 rounded-sm transition-colors ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-card border border-muted-foreground/40'}`}
          >
            {isSelected ? <CheckSquare className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : <Square className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-transparent" />}
          </span>
        )}

        {/* Version count badge in the top-right corner — hidden in edit mode so the checkbox owns the corner. */}
        {hasStack && !selectable && (
          <span className="absolute top-1.5 right-2 sm:top-2 sm:right-3 text-xs sm:text-sm font-semibold text-muted-foreground tabular-nums select-none">
            ({versionCount})
          </span>
        )}

        <div className="pt-3 pb-2.5 px-3 min-h-[88px] sm:pt-5 sm:pb-4 sm:px-4 sm:min-h-[120px] flex flex-col">
          <div className="flex items-center gap-1.5 mb-1 sm:gap-2 sm:mb-2">
            <FolderClosed className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
            <h3 className="font-semibold text-xs sm:text-sm truncate flex-1">{title}</h3>
          </div>

          <div className="text-[11px] sm:text-xs text-muted-foreground mt-auto">
            {scenarioCount} scenario{scenarioCount !== 1 ? 's' : ''}
          </div>
          {lastEdit && (
            <div className="text-[11px] sm:text-xs text-muted-foreground/70 mt-0.5">
              Edited {formatDistanceToNow(new Date(lastEdit), { addSuffix: true })}
            </div>
          )}
          {creatorName && (
            <div className="text-[11px] sm:text-xs text-muted-foreground/70 mt-0.5 truncate">
              by {creatorName}
            </div>
          )}
        </div>
      </div>
    </motion.button>
  );
}

export default function MyClientsGrid({ scenarios, drafts, onNewScenario, onClientView, onLineageOpen, isOrgView, creatorById, onMergeLineages, onDeleteLineages, onNewClientFor, advisors }: MyClientsGridProps) {
  const [search, setSearch] = useState('');
  const [sortMode, setSortModeState] = useState<SortMode>(() => readStoredSort(!!isOrgView));
  const setSortMode = (mode: SortMode) => {
    setSortModeState(mode);
    writeStoredSort(!!isOrgView, mode);
  };
  const [agentFilter, setAgentFilter] = useState<string>('all');
  // Edit / "manage folders" mode — pencil icon next to the count toggles it.
  // While on, folders become draggable (drop onto another to merge — a selected
  // drag source brings the whole selection along) AND
  // clickable for multi-select. Selected folders can be merged or deleted in bulk
  // via the action bar. Edit gates everything so normal browsing doesn't trigger
  // an accidental merge or delete.
  const [editMode, setEditMode] = useState(false);
  const [selectedRootIds, setSelectedRootIds] = useState<Set<string>>(new Set());
  const [dragSourceRoot, setDragSourceRoot] = useState<string | null>(null);
  const [dragOverRoot, setDragOverRoot] = useState<string | null>(null);
  // The set of folders that travel with a drag: when the dragged folder is part
  // of the current selection, the whole selection moves with it (drop merges
  // every selected folder into the target). Otherwise just the dragged folder.
  const dragGroupFor = (sourceRootId: string): Set<string> =>
    selectedRootIds.has(sourceRootId) ? selectedRootIds : new Set([sourceRootId]);
  const inDragGroup = (rootId: string): boolean =>
    dragSourceRoot !== null && dragGroupFor(dragSourceRoot).has(rootId);
  // Merge dialog state. mergeLineages is the ordered list to display in the radio;
  // first entry defaults as the keeper. Used by both drag-to-merge (2 entries)
  // and bulk-select-merge (N entries).
  const [mergeTarget, setMergeTarget] = useState<{
    lineages: Lineage[];
    keepRootId: string;
  } | null>(null);
  const [merging, setMerging] = useState(false);
  // Delete dialog state. lineages is the snapshot of what's being deleted;
  // typed-confirmation text gates the destructive button.
  const [deleteTarget, setDeleteTarget] = useState<{ lineages: Lineage[] } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Org-view "New Client" advisor picker.
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [newClientAdvisorId, setNewClientAdvisorId] = useState<string>('');

  const canEdit = !!onMergeLineages;
  const canBulkDelete = !!onDeleteLineages;
  const showOrgNewClient = isOrgView && !!onNewClientFor && (advisors?.length ?? 0) > 0;

  const exitEditMode = () => {
    setEditMode(false);
    setSelectedRootIds(new Set());
  };
  const toggleSelected = (rootId: string) => {
    setSelectedRootIds(prev => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId);
      else next.add(rootId);
      return next;
    });
  };

  const lineages = useMemo(() => groupLineages(scenarios, drafts, sortMode), [scenarios, drafts, sortMode]);

  // Distinct agents (creators) with at least one lineage. Only shown in org view.
  const agentOptions = useMemo(() => {
    if (!isOrgView) return [] as { id: string; label: string }[];
    const ids = new Set<string>();
    for (const l of lineages) ids.add(l.creatorId);
    return Array.from(ids).map(id => ({
      id,
      label: creatorLabel(creatorById?.get(id)),
    })).sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [lineages, isOrgView, creatorById]);

  const filtered = useMemo(() => {
    let result = lineages;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(l =>
        l.rootName.toLowerCase().includes(q) ||
        l.members.some(m => m.name.toLowerCase().includes(q)),
      );
    }
    if (isOrgView && agentFilter !== 'all') {
      result = result.filter(l => l.creatorId === agentFilter);
    }
    return result;
  }, [lineages, search, isOrgView, agentFilter]);

  const onFolderClick = (lin: Lineage) => {
    if (lin.members.length === 1) {
      // No stack — straight into the Client View for the one member.
      onClientView(lin.members[0].clientId);
      return;
    }
    // Multi-version stack — parent renders the row-list for this lineage so
    // the existing Compare / Add Scenario / Duplicate buttons are one click away.
    onLineageOpen(lin.rootId);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-xl font-semibold truncate" data-tour="clients-heading">
            {isOrgView ? `All Clients (${filtered.length})` : `My Clients (${filtered.length})`}
          </h2>
          {canEdit && (
            editMode ? (
              <Button
                size="sm"
                variant="default"
                className="h-7 px-2.5 text-xs gap-1.5"
                onClick={exitEditMode}
                title="Exit edit mode"
              >
                <Check className="h-3.5 w-3.5" /> Done
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                onClick={() => setEditMode(true)}
                title="Edit folders — select to merge or delete in bulk, or drag onto another folder to merge"
                aria-label="Edit folders"
                data-tour="clients-edit-folders"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )
          )}
        </div>
        <div className="flex items-center gap-2 flex-1 sm:flex-initial sm:justify-end flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search clients…"
              className="pl-9 h-9"
              data-tour="clients-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {isOrgView && (
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="h-9 w-[180px] shrink-0" data-tour="clients-advisor-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All agents</SelectItem>
                {agentOptions.map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={sortMode} onValueChange={(v) => setSortMode(v as SortMode)}>
            <SelectTrigger className="h-9 w-[180px] shrink-0" data-tour="clients-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="recently-edited">Most recently edited</SelectItem>
              <SelectItem value="name-asc">Name A → Z</SelectItem>
              <SelectItem value="name-desc">Name Z → A</SelectItem>
            </SelectContent>
          </Select>
          {!isOrgView && (
            <Button size="sm" onClick={onNewScenario} className="shrink-0" data-tour="new-client">
              <Plus className="h-4 w-4 mr-1" /> New Client
            </Button>
          )}
          {showOrgNewClient && (
            <Button
              size="sm"
              className="shrink-0"
              onClick={() => { setNewClientAdvisorId(''); setNewClientOpen(true); }}
              data-tour="new-client-org"
            >
              <Plus className="h-4 w-4 mr-1" /> New Client
            </Button>
          )}
        </div>
      </div>

      {/* Edit-mode selection action bar — appears once at least one folder is
          ticked. Merge needs 2+; Delete fires for any non-zero selection. */}
      {editMode && selectedRootIds.size > 0 && (
        <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2">
          <span className="text-sm font-medium">
            {selectedRootIds.size} selected
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1.5"
            disabled={selectedRootIds.size < 2}
            onClick={() => {
              const picked = filtered.filter(l => selectedRootIds.has(l.rootId));
              if (picked.length < 2) return;
              const oldest = picked.reduce((a, b) => (a.anchorCreatedAt <= b.anchorCreatedAt ? a : b));
              setMergeTarget({ lineages: picked, keepRootId: oldest.rootId });
            }}
            title={selectedRootIds.size < 2 ? 'Select at least 2 folders to merge' : 'Merge selected folders into one'}
          >
            <Users className="h-3.5 w-3.5" /> Merge
          </Button>
          {canBulkDelete && (
            <Button
              size="sm"
              variant="destructive"
              className="h-8 text-xs gap-1.5"
              onClick={() => {
                const picked = filtered.filter(l => selectedRootIds.has(l.rootId));
                if (picked.length === 0) return;
                setDeleteTarget({ lineages: picked });
                setDeleteConfirmText('');
              }}
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete ({selectedRootIds.size})
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs"
            onClick={() => setSelectedRootIds(new Set())}
          >
            Clear
          </Button>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-5 pt-2 sm:pt-3"
      >
        {filtered.length === 0 ? (
          <div className="col-span-full text-center text-sm text-muted-foreground py-12">
            {search ? 'No clients match your search.' : 'No clients yet. Click "New Client" to get started.'}
          </div>
        ) : (
          filtered.map((lin) => (
            <FolderCard
              key={lin.rootId}
              layoutId={`folder-${lin.rootId}`}
              title={lin.rootName}
              versionCount={lin.members.length}
              scenarioCount={lin.members[0]?.scenarioCount ?? 0}
              lastEdit={lin.lastEdit}
              creatorName={isOrgView ? creatorLabel(creatorById?.get(lin.creatorId)) : undefined}
              // In edit mode, clicking a folder toggles its selection (for bulk
              // merge / delete). Outside edit mode, click opens the folder.
              onClick={editMode ? () => toggleSelected(lin.rootId) : () => onFolderClick(lin)}
              draggable={editMode}
              selectable={editMode}
              isSelected={editMode && selectedRootIds.has(lin.rootId)}
              isDragSource={inDragGroup(lin.rootId)}
              isDropTarget={editMode && dragOverRoot === lin.rootId && dragSourceRoot !== null && !inDragGroup(lin.rootId)}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', lin.rootId);
                e.dataTransfer.effectAllowed = 'move';
                setDragSourceRoot(lin.rootId);
              }}
              onDragEnd={() => {
                setDragSourceRoot(null);
                setDragOverRoot(null);
              }}
              onDragEnter={(e) => {
                if (!editMode || !dragSourceRoot || inDragGroup(lin.rootId)) return;
                e.preventDefault();
                setDragOverRoot(lin.rootId);
              }}
              onDragLeave={() => {
                if (dragOverRoot === lin.rootId) setDragOverRoot(null);
              }}
              onDragOver={(e) => {
                if (!editMode || !dragSourceRoot || inDragGroup(lin.rootId)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={(e) => {
                e.preventDefault();
                const sourceRootId = e.dataTransfer.getData('text/plain');
                setDragSourceRoot(null);
                setDragOverRoot(null);
                if (!sourceRootId) return;
                // Dragging a selected folder brings the whole selection along —
                // dropping merges every selected folder into the target.
                const group = dragGroupFor(sourceRootId);
                if (group.has(lin.rootId)) return;
                const sourceLins = lineages.filter(l => group.has(l.rootId));
                if (sourceLins.length === 0) return;
                // The drop target is the default keeper — you dragged everything
                // INTO that folder. The dialog radio still allows overriding.
                setMergeTarget({
                  lineages: [...sourceLins, lin],
                  keepRootId: lin.rootId,
                });
              }}
            />
          ))
        )}
      </motion.div>

      {/* Merge confirmation — opens from drag-drop (dragged group + target) or bulk-select (N lineages). */}
      <AlertDialog open={!!mergeTarget} onOpenChange={(open) => { if (!open) setMergeTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {mergeTarget && mergeTarget.lineages.length > 2
                ? `Merge ${mergeTarget.lineages.length} folders?`
                : 'Merge two folders?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Pick which folder stays as the main one. The others become versions inside it. Every scenario stays intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {mergeTarget && (
            <RadioGroup
              value={mergeTarget.keepRootId}
              onValueChange={(v) => setMergeTarget(prev => prev ? { ...prev, keepRootId: v } : prev)}
              className="space-y-2 py-2 max-h-72 overflow-y-auto"
            >
              {[...mergeTarget.lineages]
                .sort((a, b) => a.anchorCreatedAt.localeCompare(b.anchorCreatedAt))
                .map((lin) => {
                  const total = lin.members.reduce((sum, m) => sum + m.scenarioCount, 0);
                  return (
                    <label
                      key={lin.rootId}
                      htmlFor={`merge-root-${lin.rootId}`}
                      className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <RadioGroupItem id={`merge-root-${lin.rootId}`} value={lin.rootId} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{lin.rootName}</div>
                        <div className="text-xs text-muted-foreground">
                          Created {formatDate(new Date(lin.anchorCreatedAt), 'MMM d, yyyy')} · {total} scenario{total !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </label>
                  );
                })}
            </RadioGroup>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={merging}
              onClick={async (e) => {
                e.preventDefault();
                if (!mergeTarget || !onMergeLineages) return;
                setMerging(true);
                try {
                  const keep = mergeTarget.keepRootId;
                  const drops = mergeTarget.lineages.map(l => l.rootId).filter(id => id !== keep);
                  if (drops.length > 0) {
                    await onMergeLineages(keep, drops);
                  }
                  setMergeTarget(null);
                  setSelectedRootIds(new Set());
                } finally {
                  setMerging(false);
                }
              }}
            >
              {merging ? 'Merging…' : 'Merge'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk-delete confirmation — typed "delete" gate, lists every folder being nuked. */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirmText(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.lineages.length ?? 0} folder{(deleteTarget?.lineages.length ?? 0) === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every client and scenario in the selected folders. Type
              <span className="font-semibold text-foreground"> delete </span>
              to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTarget && (
            <div className="space-y-1 max-h-48 overflow-y-auto rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
              {deleteTarget.lineages.map(lin => {
                const total = lin.members.reduce((sum, m) => sum + m.scenarioCount, 0);
                return (
                  <div key={lin.rootId} className="flex items-center justify-between gap-3 py-0.5">
                    <span className="truncate font-medium">{lin.rootName}</span>
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                      {lin.members.length} client{lin.members.length === 1 ? '' : 's'} · {total} scenario{total === 1 ? '' : 's'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="py-2">
            <Input
              autoFocus
              placeholder="Type delete to confirm…"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && deleteConfirmText.trim().toLowerCase() === 'delete' && deleteTarget && onDeleteLineages && !deleting) {
                  e.preventDefault();
                  const rootIds = deleteTarget.lineages.map(l => l.rootId);
                  setDeleting(true);
                  Promise.resolve(onDeleteLineages(rootIds))
                    .finally(() => {
                      setDeleting(false);
                      setDeleteTarget(null);
                      setDeleteConfirmText('');
                      setSelectedRootIds(new Set());
                    });
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting || deleteConfirmText.trim().toLowerCase() !== 'delete'}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async (e) => {
                e.preventDefault();
                if (!deleteTarget || !onDeleteLineages) return;
                setDeleting(true);
                try {
                  await onDeleteLineages(deleteTarget.lineages.map(l => l.rootId));
                } finally {
                  setDeleting(false);
                  setDeleteTarget(null);
                  setDeleteConfirmText('');
                  setSelectedRootIds(new Set());
                }
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Org-view "New Client" advisor picker. */}
      <Dialog open={newClientOpen} onOpenChange={setNewClientOpen}>
        <DialogContent
          data-tour="new-client-picker"
          // A walkthrough popover sits outside the dialog; a click on it must not
          // count as a click outside and close the picker mid-tour.
          onPointerDownOutside={e => { if (isTourPopover(e.target)) e.preventDefault(); }}
          onInteractOutside={e => { if (isTourPopover(e.target)) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="new-client-advisor">Create under</Label>
            <Select value={newClientAdvisorId} onValueChange={setNewClientAdvisorId}>
              <SelectTrigger id="new-client-advisor">
                <SelectValue placeholder="Choose an advisor…" />
              </SelectTrigger>
              <SelectContent>
                {(advisors ?? []).map(a => (
                  <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewClientOpen(false)}>Cancel</Button>
            <Button
              disabled={!newClientAdvisorId}
              onClick={() => {
                if (!newClientAdvisorId || !onNewClientFor) return;
                onNewClientFor(newClientAdvisorId);
                setNewClientOpen(false);
                setNewClientAdvisorId('');
              }}
            >
              Start Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
